import styled, { css } from 'styled-components'

import { Card as DefaultCard } from '@/components/ui/card'
import { hiddenScrollbar, media } from '@/theme/common'

export const Card = styled(DefaultCard)<{
  $noHorizontalPadding?: boolean
  $noShadow?: boolean
  $tableMinHeightExtra: number
}>(({ theme, $noHorizontalPadding, $noShadow, $tableMinHeightExtra }) => ({
  // Note: `flex: 1` claims any free space left over in a flex parent (e.g. Page) instead of
  // just sizing to content - stretches to fill whatever height its flex ancestors give it,
  // but never shrinks below a usable size. Once the viewport is too short even for that
  // floor, the table (and, absent any `overflow: hidden` ancestor, the whole page) scrolls.
  //
  // Note: min-height is 40rem (the table's own floor - see ui/table's Container) *plus*
  // $tableMinHeightExtra, a JS-measured (see table-group.tsx's ResizeObserver) pixel amount
  // covering whatever TitleRow/Header/Pagination actually need, plus this Card's own
  // padding/gap - NOT a flat 40rem, and deliberately NOT a CSS `min-content`/`auto` value
  // either. Both were tried and both failed:
  //
  // A flat 40rem made this Card's own explicit min-height smaller than its children's real
  // combined minimum whenever a parent (e.g. Page) had to shrink it - since an item's OWN
  // explicit min-height entirely replaces the automatic, content-based floor a flex item would
  // otherwise get, and this Card's `overflow: hidden` (from ui/card) turns that automatic floor
  // into 0 rather than content-based, nothing stopped the shrink past what
  // Header/table-floor/Pagination actually needed, silently clipping Pagination clean off the
  // bottom for an empty/loading/error table (whose own Container has its own 40rem floor,
  // pushing Card's true need past a flat 40rem) - and even for a *populated* table, that same
  // flat 40rem is a *shared* budget across Header/Table/Pagination, so the table's own visible
  // rows area ends up smaller than 40rem once Header/Pagination eat into it.
  //
  // CSS `min-content` (measuring Card's own true combined need, instead of a flat number) fixes
  // both of those, but only in isolation - ui/table's Container internally wraps a scrollable,
  // `overflow: auto` Wrapper around the actual `<table>`, and in Chrome, a `min-content`-driven
  // ancestor's intrinsic-size computation pulls in that Wrapper's *actual full row content*
  // (however many rows there are) as part of the sum, not just Container's own explicit
  // min-height floor - regardless of flex-basis tweaks on Container itself. A 203-row table
  // measured this way blew Card up to ~9900px, and a Page using the same trick to fix its own
  // analogous bottom-padding shrinkage compounded it further, to ~10700px - defeating
  // ui/table's whole internal row-scrolling mechanism. Real, measured pixels sidestep the
  // browser's `min-content` behavior entirely - nothing here asks for an intrinsic size.
  flex: 1,
  minHeight: `calc(40rem + ${$tableMinHeightExtra}px)`,
  ...($noShadow && { boxShadow: 'none' }),

  // Note: ui/card's own padding (theme.space['20']) is sized for the desktop toolbar/table
  // combo - tighter on mobile so the table itself gets more of the viewport's already-scarce
  // width, matching the cell padding reduction in ui/table below.
  [media.down('sm')]: {
    padding: theme.space['12'],
  },
  ...($noHorizontalPadding && { paddingInline: 0 }),
}))

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const Header = styled.div<{ $headerFiltersHorizontal?: boolean }>(
  ({ theme, $headerFiltersHorizontal }) => css`
    display: flex;
    flex-direction: ${$headerFiltersHorizontal ? 'row-reverse' : 'column'};
    align-items: flex-end;
    gap: ${theme.space['16']};

    /* Note: row+wrap (not column) so items that need their own full line still get one - they
       each carry an explicit width: 100% (see NavigationSearch/NavigationDatePicker/etc.) which
       claims the whole row regardless of direction - but FiltersWrapper and NavigationIconActions
       (below) deliberately don't, so wrapping packs them onto the same trailing line together
       instead of each forcing its own. Tighter row-to-row gap too, matching the padding
       reductions elsewhere in this file/ui/table for mobile's scarcer space. */
    ${media.down('sm')} {
      flex-direction: row;
      flex-wrap: wrap;
      gap: ${theme.space['8']};
    }
  `,
)

// Note: below this, status tabs are forced onto their own full-width row (see
// NavigationLeft) instead of fighting search/date/actions for space on the same line -
// tabs have no natural min-content width (overflow-x: auto collapses it to 0 per the
// flexbox auto-minimum-size rule), so without this they'd just get squeezed down to a
// clipped sliver rather than ever actually wrapping.
const NAVIGATION_STACK_BREAKPOINT = '1100px'

export const Navigation = styled.div<{ $hasLeftContent?: boolean }>(
  ({ theme, $hasLeftContent }) => css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    /* Note: space-between only when a NavigationLeft/NavigationLeftFilters sibling is actually
       rendered - its own flex: 1 claims all the leftover space and pins NavigationRight flush
       against it regardless of this value, while a lone NavigationRight that later wraps onto
       its own separate flex-wrap line still resolves flush left there (see NavigationRight's own
       comment below) to align with tabs/filters/table underneath.
       Without a NavigationLeft/NavigationLeftFilters sibling at all, NavigationRight is
       Navigation's *only* child ever, on every line - space-between has no second item to
       distribute space against and falls back to flex-start, stranding it flush left instead of
       flush right (EL-14290). flex-end has no such single-item fallback quirk, so it's used
       instead whenever there's no left-side content to begin with. */
    justify-content: ${$hasLeftContent ? 'space-between' : 'flex-end'};
    gap: ${theme.space['16']};
    flex: 1;
    /* Note: without this, Navigation's automatic minimum width falls back to the full
       min-content width of its children (since Navigation itself has no overflow that
       would zero it out) and it overflows HeaderActions instead of being constrained -
       the classic nested-flexbox truncation trap. */
    min-width: 0;

    /* Note: dropping this box (along with HeaderActions/NavigationRight below) promotes its
       children to direct flex items of Header's own column flex - which is what lets
       NavigationIconActions reorder past the later, structurally-unrelated FiltersWrapper via
       a plain flex order property instead of needing full grid-template-areas to reorder
       across a box boundary that no longer exists. */
    ${media.down('sm')} {
      display: contents;
    }
  `,
)

export const NavigationLeft = styled.div`
  ${hiddenScrollbar};

  flex: 1;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;

  @media (max-width: ${NAVIGATION_STACK_BREAKPOINT}) {
    flex-basis: 100%;
  }
`

// Note: Used in place of NavigationLeft/statusTabs when no statusTabs are passed - renders the
// `filters` content (+ Clear filters) inline on the top row instead of leaving that space empty.
export const NavigationLeftFilters = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    flex: 1;
    flex-wrap: nowrap;
    gap: ${theme.space['8']};

    /* Note: mirrors FiltersWrapper below - filter components default to filling their parent's
       width when no explicit width prop is given, which would otherwise force them onto
       separate lines here. A filter that needs a wider desktop trigger (e.g. a searchable tree
       select) should size itself via its own wrapper's CSS width, not Select's own width prop -
       that prop applies as an inline style, which no plain CSS selector here could ever override
       for the mobile full-width case (see AmenitySpaceSelectWrapper in time-blocks.styled.ts for
       the pattern - a && specificity-bump wrapper instead of fighting an inline style with
       !important). */
    > *:not([data-clear-filters-button]) {
      flex-shrink: 0;
      width: 16rem;

      /* Note: the fixed 16rem is for lining several filters up evenly side by side on desktop -
         below sm each filter goes full-width instead (see the flex-wrap: wrap override below),
         so a fixed width just leaves dead space beside whichever one is alone on its line
         instead of using it. */
      ${media.down('sm')} {
        width: 100%;
      }
    }

    /* Note: mirrors FiltersWrapper's own DatePickerInput exception below - the generic 16rem
       above is too narrow to fit even a date-range placeholder. */
    > [data-component-id='DatePickerInput'] {
      width: 22.5rem;

      ${media.down('sm')} {
        width: 100%;
      }
    }

    /* Note: base flex-wrap is nowrap (desktop keeps filters side by side even if they'd
       otherwise overflow, matching this element's own flex: 1 sizing off whatever room the rest
       of the row leaves it) - below sm, with each filter now full-width and flex-shrink: 0
       (can't shrink to fit), nowrap would force them to overflow the row instead of stacking, so
       mobile switches to wrap. flex-basis: 100% forces this onto its own dedicated line in
       Header's outer row-wrap, deterministically (see the responsive-design skill, §3) - a plain
       width: 100% is NOT enough here: the base flex: 1 above already sets flex-basis to 0%, and
       width only acts as a flex-basis fallback when flex-basis is auto, so width: 100% alone is
       silently ignored as a sizing basis. flex-grow/flex-shrink stay at 1 (matching the base
       flex: 1) so this can still shrink below 100% on wider mobile widths if ever needed - only
       the basis changes. */
    ${media.down('sm')} {
      flex-wrap: wrap;
      flex-basis: 100%;
    }
  `,
)

export const NavigationFilters = styled.div`
  flex-shrink: 0;
  width: 16rem;
  max-width: 100%;

  ${media.down('sm')} {
    width: 100%;
  }
`

export const NavigationRight = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.space['8']};
    /* Note: deliberately NOT margin-left: auto - Navigation's own justify-content: space-between
       (below) already pushes this to the row's right edge when it shares a line with
       NavigationLeft (whose flex: 1 fills the leftover space anyway). margin-left: auto doesn't
       respect flex-wrap line boundaries the same way though - when the combined width forces
       this onto its own wrapped line (alone), auto margin still claims 100% of that line's free
       space and shoves it flush right, instead of the flex-start position space-between resolves
       a lone line-item to. That mismatch was leaving search/date/the icon group stranded on the
       right with a large empty gap on the left, out of alignment with every other row (tabs,
       filters, table) which all start flush left.
       Note: no flex-shrink: 0 here (unlike the fixed-width children below) - this needs to
       be allowed to shrink down to whatever space Navigation actually gives it, otherwise it
       just renders at its full unwrapped content width and overflows instead of letting its
       own flex-wrap reflow search/date/the icon group onto separate rows. min-width: 0 for
       the same nested-flexbox reason as Navigation above. */
    flex-shrink: 1;
    min-width: 0;

    /* Note: search/date-picker/icon-actions each go width:100% (or, for icon-actions, rely on
       Header's align-items: flex-end) once this box - along with HeaderActions/Navigation above
       - drops out via display: contents, promoting them to direct flex items of Header's own
       column flex. That's also what lets NavigationIconActions reorder past FiltersWrapper (a
       structurally unrelated later sibling of HeaderActions, not reachable from in here) via a
       plain order property - see NavigationIconActions below. */
    ${media.down('sm')} {
      display: contents;
    }
  `,
)

export const NavigationSearch = styled.div(
  ({ theme }) => css`
    flex-shrink: 0;
    width: ${theme.space['220']};
    max-width: 100%;

    ${media.down('sm')} {
      width: 100%;
    }
  `,
)

// Note: fine-tuned literal (not a theme.space token) - this is a one-off width to fit
// the date-range display text, not part of the spacing rhythm the scale governs.
export const NavigationDatePicker = styled.div`
  flex-shrink: 0;
  width: 22.5rem;
  max-width: 100%;

  ${media.down('sm')} {
    width: 100%;
  }

  /* Note: tables that render navigation.search (Reservations) keep the default full-width row
     above - search already claims its own full-width line regardless, so sharing this line with
     NavigationIconActions would still leave the icon-actions button crammed onto the same
     already-tight row as this. Tables with no search slot at all (Time Blocks) get the
     alternative treatment below instead: same mobile pattern FiltersWrapper's own filter
     children already use for Reservations' real date-range filter - width: auto + flex: 1 1 0
     (not width: 100%) so this shares its row with NavigationIconActions instead of forcing its
     own full line, once whatever comes before it (NavigationLeftFilters, full-width on its own
     line) has been eliminated from this flex-wrap line. flex-basis: 0 so it only ever grows to
     fill whatever NavigationIconActions' fixed width leaves, never competing with it for room.
     min-width: 0 is safe to shrink freely since the rendered value is a real <input>, which
     clips/scrolls its own text natively when narrower than its content.
     :has() (not a new prop) is what tells the two cases apart, keyed off whether
     NavigationRight actually contains a NavigationSearch - purely structural, no extra API
     surface on TableGroup. */
  ${NavigationRight}:not(:has(${NavigationSearch})) & {
    ${media.down('sm')} {
      width: auto;
      flex: 1 1 0;
      min-width: 0;
    }
  }
`

// Note: the small square icon buttons (export/download, filter toggle, column visibility)
// must move together as a single unit - never split across rows - so they're grouped into
// one flex-nowrap item here rather than left as separate top-level children of
// NavigationRight, which would let each button wrap independently.
export const NavigationIconActions = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: ${theme.space['8']};
    flex-shrink: 0;

    /* Note: once Navigation/NavigationRight flatten via display: contents (above), this sits
       ahead of FiltersWrapper in DOM order (icon actions render inside HeaderActions, filters
       render in Header's own later, separate FiltersWrapper child) - order:1 pushes it after
       FiltersWrapper's default order:0 instead. Every other flattened item stays default order,
       so they keep their existing relative sequence among themselves.

       margin-left: auto keeps this pinned to the row's right edge whether or not FiltersWrapper
       ends up sharing the line with it - when it does, FiltersWrapper's own flex-grow already
       claims all the leftover space so the margin has nothing left to push against (a no-op);
       when this is alone on its line (no filters prop passed), the margin is what right-aligns
       it, same as NavigationRight's justify-content: flex-end did before this flattened. */
    ${media.down('sm')} {
      order: 1;
      margin-left: auto;
    }
  `,
)

export const HeaderActions = styled.div(
  ({ theme }) => css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: ${theme.space['8']};
    width: 100%;

    ${media.down('sm')} {
      display: contents;
    }
  `,
)

export const ActionButton = styled.button<{ $isActive: boolean }>(
  ({ theme, $isActive }) => css`
    align-items: center;
    background: ${$isActive ? theme.colors.black500 : theme.colors.white};
    border: ${theme.border.size['1']} solid
      ${$isActive ? theme.colors.black500 : theme.colors.gray400};
    border-radius: ${theme.border.radius['4']};
    color: ${$isActive ? theme.colors.white : theme.colors.black500};
    cursor: pointer;
    display: flex;
    height: 3.2rem;
    justify-content: center;
    transition:
      background-color ${theme.transition.duration.sm} ease,
      border-color ${theme.transition.duration.sm} ease;
    width: 3.2rem;
    flex-shrink: 0;

    &:hover {
      background: ${theme.colors.black500};
      border-color: ${theme.colors.black500};
      color: ${theme.colors.white};
    }
  `,
)

export const ActionButtonWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.space['8']};
    width: ${theme.space['32']};
  `,
)

export const FiltersWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: ${theme.space['8']};
    width: 100%;

    /* Note: This ensures that all filters passed to table group will have a fixed width */
    > *:not([data-clear-filters-button]) {
      width: 16rem;

      ${media.down('sm')} {
        width: 100%;
      }
    }

    /* Note: a date-range filter (e.g. the resident-scoped embeds in Packages/Checklists that
       move their date range out of the navigation row and into here) needs more room than the
       generic 16rem above - not enough to fit even the placeholder, let alone a formatted
       range like "08/19/2026 - 09/18/2026". Matches NavigationDatePicker's width for the same
       control rendered in the navigation row instead. Same specificity as the rule above (one
       attribute selector each) so declaration order - this coming second - is what makes it
       win, rather than an explicit :not() exclusion up there. */
    > [data-component-id='DatePickerInput'] {
      width: 22.5rem;

      ${media.down('sm')} {
        width: 100%;
      }
    }

    /* Note: width: auto + flex: 1 (not width: 100%) so this shares its row with
       NavigationIconActions - see Header's row+wrap above - instead of forcing its own full
       line. The filter control inside still gets width: 100% of whatever this ends up computing
       (the space NavigationIconActions doesn't take), not of the row itself.

       flex-basis: 0 (not e.g. 20rem) - a non-zero basis is a claim this needs to shrink *from*,
       competing with NavigationIconActions for room even at true phone widths (~375px) and
       forcing the pair to wrap after all despite flex-shrink allowing it to give way. Starting
       from 0 means it only ever grows to fill whatever NavigationIconActions' fixed width
       actually leaves, so there's nothing to shrink and nothing to lose the fit over. */
    ${media.down('sm')} {
      width: auto;
      flex: 1 1 0;
      min-width: 0;
    }
  `,
)
